/**
 * CandidateCompareDrawer — full-screen side-by-side comparison of up to 10
 * candidates across every meaningful attribute.
 *
 * Renders as an ecommerce-style comparison grid: one candidate per column,
 * one attribute per row. Values that differ light up (highlighted background)
 * so recruiters can spot the diffs at a glance. Includes a "highlight
 * differences only" toggle to compress the view when many rows agree.
 *
 * Data source: the array of application objects passed in via `apps`. Each
 * carries the full candidate + screening_profile + analysis + budget_status
 * already fetched by the parent — no new API calls.
 */
import { useMemo, useState } from "react";
import { X, Star, Award, Trophy, Check, Minus, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { formatCompCurrency, budgetLabel, budgetBadgeClass } from "@/lib/budget";

// ────────────────────────── Attribute catalog ──────────────────────────
// Each row of the compare table pulls values via `get(app)`. `fmt` controls
// how the value is rendered; `highlight` (optional) picks the best value in
// the row so we can crown a winner with a trophy icon.
const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
};

const pickComp = (c) => {
    const sp = c?.screening_profile || {};
    return {
        currency: sp.ctc_currency || c?.salary_currency || "INR",
        current: num(sp.current_ctc_annual) ?? num(c?.current_salary),
        desired: num(sp.desired_ctc_annual) ?? num(c?.desired_salary_max) ?? num(c?.desired_salary_min),
        increment: num(sp.desired_increment_pct),
    };
};

const ATTR_GROUPS = [
    { label: "AI Insights", rows: [
        { key: "score", label: "Match score", get: (a) => a.match_score ?? null, fmt: (v) => v == null ? "—" : `${v}`, highlight: "max" },
        { key: "red_flags_count", label: "Red flags", get: (a) => (a.analysis?.red_flags || []).length, fmt: (v) => v == null ? "—" : `${v}`, highlight: "min" },
        { key: "ai_summary", label: "AI summary", get: (a) => a.ai_summary || a.analysis?.summary || "", fmt: (v) => v || "—", wrap: true },
    ]},
    { label: "Identity", rows: [
        { key: "email", label: "Email", get: (a) => a.candidate?.email, fmt: (v) => v || "—" },
        { key: "phone", label: "Phone", get: (a) => a.candidate?.phone, fmt: (v) => v || "—" },
        { key: "location", label: "Location", get: (a) => a.candidate?.location, fmt: (v) => v || "—" },
        { key: "current_title", label: "Current title", get: (a) => a.candidate?.current_title, fmt: (v) => v || "—" },
        { key: "current_company", label: "Current company", get: (a) => a.candidate?.current_company, fmt: (v) => v || "—" },
        { key: "yoe", label: "Years of experience", get: (a) => a.candidate?.total_experience_years, fmt: (v) => v == null ? "—" : `${v} yrs`, highlight: "max" },
    ]},
    { label: "Compensation", rows: [
        { key: "current_ctc", label: "Current CTC (Annual)", get: (a) => pickComp(a.candidate).current,
          fmt: (v, a) => v == null ? "—" : formatCompCurrency(v, pickComp(a.candidate).currency), highlight: "min" },
        { key: "current_ctc_monthly", label: "Current CTC (Monthly)", get: (a) => { const c = pickComp(a.candidate); return c.current == null ? null : c.current / 12; },
          fmt: (v, a) => v == null ? "—" : `${formatCompCurrency(v, pickComp(a.candidate).currency)}/mo` },
        { key: "desired_ctc", label: "Desired CTC (Annual)", get: (a) => pickComp(a.candidate).desired,
          fmt: (v, a) => v == null ? "—" : formatCompCurrency(v, pickComp(a.candidate).currency), highlight: "min" },
        { key: "desired_ctc_monthly", label: "Desired CTC (Monthly)", get: (a) => { const c = pickComp(a.candidate); return c.desired == null ? null : c.desired / 12; },
          fmt: (v, a) => v == null ? "—" : `${formatCompCurrency(v, pickComp(a.candidate).currency)}/mo` },
        { key: "increment", label: "Increment %", get: (a) => {
              const c = pickComp(a.candidate);
              if (c.increment != null) return c.increment;
              if (c.current && c.desired) return Math.round(((c.desired - c.current) / c.current) * 1000) / 10;
              return null;
          }, fmt: (v) => v == null ? "—" : `${v}%`, highlight: "min" },
        { key: "budget", label: "Budget check", get: (a) => a.budget_status?.status,
          fmt: (v) => v ? <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${budgetBadgeClass(v)}`}>{budgetLabel(v)}</span> : "—" },
    ]},
    { label: "Availability", rows: [
        { key: "notice_days", label: "Notice period (days)", get: (a) => (a.candidate?.screening_profile || {}).notice_period_days ?? a.candidate?.notice_period_days, fmt: (v) => v == null ? "—" : `${v}`, highlight: "min" },
        { key: "earliest_join", label: "Earliest join date", get: (a) => (a.candidate?.screening_profile || {}).earliest_join_date, fmt: (v) => v || "—" },
        { key: "on_notice", label: "On notice?", get: (a) => (a.candidate?.screening_profile || {}).on_notice_period, fmt: (v) => v || "—" },
        { key: "relocate", label: "Ready to relocate?", get: (a) => {
              const sp = a.candidate?.screening_profile || {};
              return sp.ready_to_relocate ?? (a.candidate?.willing_to_relocate === true ? "Yes" : a.candidate?.willing_to_relocate === false ? "No" : null);
          }, fmt: (v) => v == null ? "—" : (typeof v === "boolean" ? (v ? "Yes" : "No") : v) },
    ]},
    { label: "Work Preferences", rows: [
        { key: "prev_work_mode", label: "Previous work mode", get: (a) => (a.candidate?.screening_profile || {}).previous_work_mode, fmt: (v) => v || "—" },
        { key: "offered_work_mode", label: "Preferred work mode", get: (a) => (a.candidate?.screening_profile || {}).offered_work_mode, fmt: (v) => v || "—" },
        { key: "led_team", label: "Led team before?", get: (a) => (a.candidate?.screening_profile || {}).has_led_team, fmt: (v) => v || "—" },
        { key: "job_hopper", label: "Job hopper?", get: (a) => (a.candidate?.screening_profile || {}).is_job_hopper, fmt: (v) => v || "—" },
        { key: "reason_change", label: "Reason for change", get: (a) => (a.candidate?.screening_profile || {}).reason_for_job_change, fmt: (v) => v || "—", wrap: true },
        { key: "cultural_fit", label: "Cultural fit rating", get: (a) => (a.candidate?.screening_profile || {}).cultural_fit_rating, fmt: (v) => v == null ? "—" : `${v}/5`, highlight: "max" },
    ]},
    { label: "Sourcing", rows: [
        { key: "source", label: "Source", get: (a) => (a.candidate?.screening_profile || {}).application_source ?? a.candidate?.application_source ?? a.source, fmt: (v) => v || "—" },
        { key: "referred_by", label: "Referred by", get: (a) => (a.candidate?.screening_profile || {}).referred_by_name ?? a.candidate?.referred_by, fmt: (v) => v || "—" },
        { key: "applied_on", label: "Applied on", get: (a) => a.created_at, fmt: (v) => v ? v.slice(0, 10) : "—" },
    ]},
    { label: "Skills", rows: [
        { key: "matched_skills", label: "Matched skills", get: (a) => (a.analysis?.matched_skills || a.analysis?.must_have_analysis?.matched || []).length, fmt: (v) => v == null ? "—" : `${v}`, highlight: "max" },
        { key: "missing_skills", label: "Missing skills", get: (a) => (a.analysis?.missing_skills || a.analysis?.must_have_analysis?.missing || []).length, fmt: (v) => v == null ? "—" : `${v}`, highlight: "min" },
        { key: "skills_list", label: "Skill inventory", get: (a) => (a.candidate?.skills || []).slice(0, 10).join(", "), fmt: (v) => v || "—", wrap: true },
    ]},
    { label: "Team feedback", rows: [
        { key: "team_rating", label: "Team rating", get: (a) => a.recruiter_rating?.avg, fmt: (v) => v == null ? "—" : `${v}/5`, highlight: "max" },
        { key: "rating_count", label: "Number of ratings", get: (a) => a.recruiter_rating?.count, fmt: (v) => v == null ? "—" : `${v}`, highlight: "max" },
        { key: "stage", label: "Current stage", get: (a) => a.stage, fmt: (v) => v || "Applied" },
    ]},
];

// ────────────────────────── Row winner logic ──────────────────────────
function findWinner(rowValues, mode) {
    if (!mode) return -1;
    const nums = rowValues.map((v) => (typeof v === "number" ? v : null));
    if (!nums.some((v) => v != null)) return -1;
    const filtered = nums.map((v) => (v == null ? (mode === "max" ? -Infinity : Infinity) : v));
    const best = mode === "max" ? Math.max(...filtered) : Math.min(...filtered);
    // Refuse to pick a winner if the "best" equals the sentinel (all nulls).
    if (best === -Infinity || best === Infinity) return -1;
    return filtered.indexOf(best);
}

// Whether every non-null value is equal (used for "highlight only differences").
function allEqual(values) {
    const distinct = new Set(values.map((v) => (v == null ? "__null__" : JSON.stringify(v))));
    return distinct.size <= 1;
}

// ────────────────────────── Component ──────────────────────────
export default function CandidateCompareDrawer({ apps = [], job, onClose }) {
    const [diffsOnly, setDiffsOnly] = useState(false);

    // Cache the per-row raw values so we can compute winner + diff detection
    // without recomputing on every render.
    const rows = useMemo(() => {
        const output = [];
        for (const g of ATTR_GROUPS) {
            const groupRows = [];
            for (const r of g.rows) {
                const values = apps.map((a) => {
                    try { return r.get(a); } catch { return null; }
                });
                groupRows.push({
                    key: r.key,
                    label: r.label,
                    values,
                    formatted: apps.map((a, i) => (r.fmt ? r.fmt(values[i], a) : values[i])),
                    winner: findWinner(values, r.highlight),
                    identical: allEqual(values),
                    wrap: !!r.wrap,
                });
            }
            output.push({ label: g.label, rows: groupRows });
        }
        return output;
    }, [apps]);

    const displayGroups = useMemo(() => {
        if (!diffsOnly) return rows;
        return rows
            .map((g) => ({ ...g, rows: g.rows.filter((r) => !r.identical) }))
            .filter((g) => g.rows.length > 0);
    }, [rows, diffsOnly]);

    const exportCSV = () => {
        const header = ["Attribute", ...apps.map((a) => (a.candidate?.name || "Candidate").replace(/,/g, " "))];
        const lines = [header.join(",")];
        for (const g of rows) {
            for (const r of g.rows) {
                const cells = r.values.map((v) => {
                    if (v === null || v === undefined) return "";
                    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
                    return `"${s.replace(/"/g, '""')}"`;
                });
                lines.push([`"${r.label}"`, ...cells].join(","));
            }
        }
        const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `candidate_compare_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(a.href);
    };

    if (!apps.length) return null;

    return (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-stretch justify-center p-4" role="dialog" aria-modal="true" data-testid="candidate-compare-drawer">
            <div className="bg-background border border-border w-full max-w-[1600px] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="px-4 py-3 border-b border-border flex items-center gap-3 bg-white">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Candidate compare</div>
                        <h2 className="font-display font-black text-xl leading-tight">
                            {apps.length} candidates side-by-side
                            {job?.title && <span className="text-muted-foreground text-sm font-normal ml-2">· {job.title}</span>}
                        </h2>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="compare-diff-toggle">
                            <input type="checkbox" checked={diffsOnly} onChange={(e) => setDiffsOnly(e.target.checked)} className="accent-ikb" />
                            <span>Show differences only</span>
                        </label>
                        <button type="button" onClick={exportCSV}
                            className="h-8 px-2 border border-border text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:border-ikb hover:text-ikb"
                            data-testid="compare-export-csv">
                            <Download className="w-3 h-3" /> CSV
                        </button>
                        <button type="button" onClick={onClose}
                            className="h-8 w-8 inline-flex items-center justify-center border border-border hover:border-ikb hover:text-ikb"
                            aria-label="Close compare view" data-testid="compare-close-btn">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Candidate headers row (sticky within scroll) */}
                <div className="overflow-auto pw-thin-scroll flex-1">
                    <table className="border-collapse text-xs w-full">
                        <thead>
                            <tr>
                                <th className="sticky top-0 left-0 z-30 bg-secondary/60 border-b border-r border-border text-left px-3 py-2 font-mono uppercase tracking-widest text-[10px]"
                                    style={{ minWidth: 220, width: 220 }}>
                                    Attribute
                                </th>
                                {apps.map((a, i) => (
                                    <th key={a.id}
                                        className="sticky top-0 z-20 bg-white border-b border-r border-border p-3 text-left align-top"
                                        style={{ minWidth: 240, width: 240 }}>
                                        <CandidateHeader app={a} index={i} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {displayGroups.length === 0 ? (
                                <tr>
                                    <td colSpan={apps.length + 1} className="p-8 text-center text-sm text-muted-foreground">
                                        Every attribute is identical across these candidates. Uncheck "Show differences only" to see the full comparison.
                                    </td>
                                </tr>
                            ) : displayGroups.map((g) => (
                                <RowGroup key={g.label} group={g} apps={apps} />
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="px-4 py-2 border-t border-border bg-secondary/20 text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2 flex-wrap">
                    <Trophy className="w-3 h-3 text-amber-500" /> Trophy = best in row
                    <span>·</span>
                    <span className="inline-block w-2.5 h-2.5 bg-emerald-200/70" /> Winner cell
                    <span>·</span>
                    <span className="inline-block w-2.5 h-2.5 bg-ikb/10" /> Different values
                    <span className="ml-auto">Rendered from local payload — no additional API calls</span>
                </div>
            </div>
        </div>
    );
}

function CandidateHeader({ app, index }) {
    const c = app?.candidate || {};
    const initials = (c.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
    const score = app?.match_score || 0;
    return (
        <div className="flex flex-col gap-1" data-testid={`compare-header-${index}`}>
            <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-ikb text-white grid place-items-center font-display font-black text-sm shrink-0">{initials}</div>
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{c.name || "Unknown"}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                        {c.current_title || "—"}
                        {c.current_company && <> · {c.current_company}</>}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono">
                <span className={`px-1.5 py-0.5 border ${score >= 75 ? "border-emerald-500 bg-emerald-50 text-emerald-800" : score >= 50 ? "border-amber-500 bg-amber-50 text-amber-800" : "border-rose-400 bg-rose-50 text-rose-800"}`}>
                    <Award className="w-3 h-3 inline mr-0.5" /> {score}
                </span>
                {app.stage && <span className="px-1.5 py-0.5 border border-border">{app.stage}</span>}
                {c.email && (
                    <button className="ml-auto hover:text-ikb inline-flex items-center gap-1 text-muted-foreground"
                        onClick={() => { navigator.clipboard.writeText(c.email); toast.success("Email copied"); }}
                        title={c.email}>
                        <Copy className="w-3 h-3" />
                    </button>
                )}
            </div>
        </div>
    );
}

function RowGroup({ group, apps }) {
    return (
        <>
            <tr>
                <td colSpan={apps.length + 1}
                    className="bg-secondary/40 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                    {group.label}
                </td>
            </tr>
            {group.rows.map((r) => (
                <tr key={r.key} className="border-b border-border">
                    <td className="sticky left-0 bg-white border-r border-border px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-foreground/70 align-top"
                        style={{ minWidth: 220, width: 220 }}>
                        {r.label}
                    </td>
                    {apps.map((a, i) => {
                        const isWinner = r.winner === i;
                        const isDiff = !r.identical;
                        const cls = isWinner ? "bg-emerald-100/70" : isDiff ? "bg-ikb/5" : "";
                        return (
                            <td key={`${r.key}-${i}`}
                                className={`border-r border-border px-3 py-2 align-top ${r.wrap ? "" : "whitespace-nowrap overflow-hidden text-ellipsis"} ${cls}`}
                                style={{ minWidth: 240, width: 240, maxWidth: 240 }}
                                data-testid={`compare-cell-${r.key}-${i}`}>
                                <div className="text-xs leading-relaxed flex items-start gap-1">
                                    {isWinner && <Trophy className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />}
                                    <div className={r.wrap ? "whitespace-normal" : "truncate"}>
                                        {typeof r.formatted[i] === "string" || typeof r.formatted[i] === "number"
                                            ? r.formatted[i]
                                            : r.formatted[i]}
                                    </div>
                                </div>
                            </td>
                        );
                    })}
                </tr>
            ))}
        </>
    );
}
